import React from "react";
import { Link } from "react-router-dom";
import Alert from "web-ui/components/internal/Alert";
import Button from "web-ui/components/internal/Button";
import Drawer from "web-ui/components/internal/Drawer";
import Dropdown from "web-ui/components/internal/Dropdown";
import Icon, { iconFactory } from "web-ui/components/internal/Icon";
import Menu from "web-ui/components/internal/Menu";
import MenuItem from "web-ui/components/internal/MenuItem";
import Table from "web-ui/components/internal/Table";
import TextInput from "web-ui/components/internal/TextInput";

import { type VoiceListVoice } from "../../api/generalTts";
import type { VoicesV2Item } from "../../api/ttsGenerationBackend";
import {
  getCustomVoiceEndpointStatuses,
  getTtsGenerationAudio,
  spinUpCustomVoiceEndpoint,
} from "../../api/ttsGenerationBackend";

import AccordionPanel from "./AccordionPanel";
import EditVoiceModal from "./EditVoiceModal";
import styles from "./VoiceDrawer.module.scss";
import {
  formatCharacter,
  formatLanguage,
  formatProvider,
  titleCaseFormatter,
} from "./VoiceFinder";

interface Props {
  className?: string;
  exampleSentence?: string;
  open?: boolean;
  onChange?: (
    type: "clone" | "edit",
    voice: VoicesV2Item,
    prevVoice: VoiceListVoice,
  ) => void;
  onRequestClose?: () => void;
  voice?: VoiceListVoice;
}

const VoiceDrawer = ({
  className,
  exampleSentence,
  onChange,
  onRequestClose,
  open,
  voice,
}: Props) => {
  const [showMenu, setShowMenu] = React.useState(false);
  const [showModal, setShowModal] = React.useState<"clone" | "edit" | false>(
    false,
  );
  return (
    <>
      <Drawer
        className={className}
        headerRight={
          <div className={styles["menu-container"]}>
            <Button<"button">
              isGhost={true}
              isIconOnly={true}
              onClick={e => {
                // If the dropdown is shown, clicks outside it are handled by its
                // closing mechanism.
                if (
                  showMenu &&
                  (e.nativeEvent as PointerEvent).pointerId !== -1
                ) {
                  return;
                }
                setShowMenu(!showMenu);
              }}
              size="s"
            >
              <Icon name="ellipsis-vertical" size="s" />
            </Button>
            {showMenu ? (
              <Dropdown
                className={styles["menu-dropdown"]}
                onRequestClose={() => {
                  // If clicking on the reference button, allow that click to be processed
                  // before closing the dropdown.
                  setTimeout(() => setShowMenu(false), 0);
                }}
                type="menu"
              >
                <Menu className={styles.menu}>
                  <MenuItem
                    leadingVisual={iconFactory("edit")}
                    onClick={() => {
                      setShowModal("edit");
                      setShowMenu(false);
                    }}
                  >
                    Edit voice
                  </MenuItem>
                  <MenuItem
                    leadingVisual={iconFactory("foreground")}
                    onClick={() => {
                      setShowModal("clone");
                      setShowMenu(false);
                    }}
                  >
                    Clone voice
                  </MenuItem>
                </Menu>
              </Dropdown>
            ) : null}
          </div>
        }
        headerText={voice?.name}
        modal={false}
        onRequestClose={() => {
          if (!showModal) {
            onRequestClose?.();
          }
        }}
        open={open}
      >
        <VoiceDrawerContents
          exampleSentence={exampleSentence}
          key={voice?.name}
          voice={voice}
        />
      </Drawer>
      {showModal && voice ? (
        <EditVoiceModal
          mode={showModal}
          onChange={(mode, clone) => {
            onChange?.(mode, clone, voice);
            setShowModal(false);
          }}
          onRequestClose={() => setShowModal(false)}
          voice={voice}
        />
      ) : null}
    </>
  );
};

// eslint-disable-next-line complexity
const VoiceDrawerContents = ({
  exampleSentence = "",
  voice,
}: Pick<Props, "exampleSentence" | "voice">) => {
  const [azureError, setAzureError] = React.useState<string>();
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [previewText, setPreviewText] = React.useState(exampleSentence);

  const isUnavailable = voice?.status === "unavailable";

  const createEvaluationParams = React.useMemo(() => {
    if (!voice) {
      return undefined;
    }
    const params = new URLSearchParams();
    if (voice.primary_language !== null) {
      params.set("languageId", voice.primary_language.split("-")[0]);
    }
    params.set("provider", voice.provider);
    params.set("voiceName", voice.name);
    return params;
  }, [voice]);

  const handlePlay = async () => {
    if (!voice) {
      return;
    }
    setIsPlaying(true);
    try {
      if (voice.world_character && voice.provider === "azure") {
        const status = await (async () => {
          // It looks like the backend returns the alias when fetching by voice name,
          // so check both to be sure.
          const statuses = await getCustomVoiceEndpointStatuses([
            voice.name,
            voice.provider_parameters.model_name,
          ]);
          return (
            statuses[voice.name] ??
            statuses[voice.provider_parameters.model_name]
          );
        })();
        switch (status) {
          case "ACTIVE":
            setAzureError(undefined);
            break;
          case "SPINNING_DOWN":
          case "SUSPENDED":
            spinUpCustomVoiceEndpoint(voice.name);
            setAzureError("Spinning up…, try again in a bit");
            return;
          case "SPINNING_UP":
            setAzureError("Spinning up…, try again in a bit");
            return;
          case "UNKNOWN":
            setAzureError("Unknown error");
            return;
          default:
            return;
        }
      } else {
        setAzureError(undefined);
      }
      const mp3Data = await getTtsGenerationAudio({
        languageId: voice.primary_language as string,
        text: previewText,
        voiceName: voice.name,
      });
      const url = URL.createObjectURL(mp3Data);
      const audio = new Audio();
      audio.src = url;
      audio.addEventListener("ended", () => URL.revokeObjectURL(url));
      await audio.play();
    } finally {
      setIsPlaying(false);
    }
  };

  return (
    <>
      {isUnavailable ? (
        <Alert
          className={styles.alert}
          color="amber"
          description="Voice is marked unavailable by provider"
          leadingVisual={iconFactory("warning")}
        />
      ) : null}
      <div className={styles.panels}>
        <AccordionPanel header="Preview audio">
          <div className={styles.preview}>
            <Button
              isIconOnly={true}
              onClick={handlePlay}
              size="s"
              state={
                isPlaying
                  ? "loading"
                  : !isUnavailable && voice?.primary_language && previewText
                    ? "enabled"
                    : "disabled"
              }
            >
              <Icon color="inherit" name="play" size="s" variant="inverse" />
            </Button>
            <TextInput
              className={styles.input}
              helperText={azureError}
              onChange={e => setPreviewText(e.value)}
              placeholder="Add text to preview the voice"
              size="s"
              state={azureError === undefined ? "enabled" : "error"}
              value={previewText}
            />
          </div>
        </AccordionPanel>
        {voice === undefined ? null : (
          <AccordionPanel header="About">
            <Table className={styles.table} outerBorder={false} size="s">
              <tbody>
                <tr>
                  <td>Voice name</td>
                  <td className={styles["table-value"]}>{voice.name}</td>
                </tr>
                <tr>
                  <td>Model ID</td>
                  <td className={styles["table-value"]}>
                    {voice.provider_parameters.model_name}
                  </td>
                </tr>
                <tr>
                  <td>Provider</td>
                  <td className={styles["table-value"]}>
                    {formatProvider(voice.provider)}
                  </td>
                </tr>
                <tr>
                  <td>Language</td>
                  <td className={styles["table-value"]}>
                    {formatLanguage(voice.primary_language)}
                  </td>
                </tr>
                <tr>
                  <td>Multilingual</td>
                  <td className={styles["table-value"]}>
                    {voice.is_multilingual ? "Yes" : "No"}
                  </td>
                </tr>
                <tr>
                  <td>Gender</td>
                  <td className={styles["table-value"]}>
                    {voice.gender === null
                      ? "N/A"
                      : titleCaseFormatter(voice.gender)}
                  </td>
                </tr>
                <tr>
                  <td>Character</td>
                  <td className={styles["table-value"]}>
                    {voice.world_character === null
                      ? "N/A"
                      : formatCharacter(voice.world_character)}
                  </td>
                </tr>
                <tr>
                  <td>Age</td>
                  <td className={styles["table-value"]}>
                    {voice.age === null ? "N/A" : titleCaseFormatter(voice.age)}
                  </td>
                </tr>
                {voice.provider_parameters.rate === undefined ? null : (
                  <tr>
                    <td>Rate</td>
                    <td className={styles["table-value"]}>
                      {voice.provider_parameters.rate ?? "N/A"}
                    </td>
                  </tr>
                )}
                {voice.provider_parameters.pitch === undefined ? null : (
                  <tr>
                    <td>Pitch</td>
                    <td className={styles["table-value"]}>
                      {voice.provider_parameters.pitch ?? "N/A"}
                    </td>
                  </tr>
                )}
                {voice.provider_parameters.volume === undefined ? null : (
                  <tr>
                    <td>Volume</td>
                    <td className={styles["table-value"]}>
                      {voice.provider_parameters.volume ?? "N/A"}
                    </td>
                  </tr>
                )}
                {voice.provider_parameters.similarity_boost ===
                undefined ? null : (
                  <tr>
                    <td>Similarity boost</td>
                    <td className={styles["table-value"]}>
                      {voice.provider_parameters.similarity_boost ?? "N/A"}
                    </td>
                  </tr>
                )}
                {voice.provider_parameters.stability === undefined ? null : (
                  <tr>
                    <td>Stability</td>
                    <td className={styles["table-value"]}>
                      {voice.provider_parameters.stability ?? "N/A"}
                    </td>
                  </tr>
                )}
                {voice.provider_parameters.style === undefined ? null : (
                  <tr>
                    <td>Style</td>
                    <td className={styles["table-value"]}>
                      {voice.provider_parameters.style ?? "N/A"}
                    </td>
                  </tr>
                )}
                {voice.provider_parameters.use_speaker_boost ===
                undefined ? null : (
                  <tr>
                    <td>Use speaker boost</td>
                    <td className={styles["table-value"]}>
                      {(() => {
                        switch (voice.provider_parameters.use_speaker_boost) {
                          case true:
                            return "Yes";
                          case false:
                            return "No";
                        }
                        return "N/A";
                      })()}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </AccordionPanel>
        )}
        <AccordionPanel header="Quality">
          <span className={styles["text-empty"]}>
            This section hasn&apos;t been implemented yet
          </span>
        </AccordionPanel>
        <AccordionPanel header="Evaluations">
          <Link
            className={styles.link}
            target="_blank"
            to={`https://tts-evaluation.duolingo.com/admin/create-evaluation-v2?${createEvaluationParams?.toString()}`}
          >
            Create new
          </Link>
        </AccordionPanel>
        {voice === undefined ? null : (
          <>
            <AccordionPanel header="Notes">
              <span
                className={voice.notes ? styles.text : styles["text-empty"]}
              >
                {voice.notes || "No notes added"}
              </span>
            </AccordionPanel>
            <AccordionPanel header="History">
              <Table className={styles.table} outerBorder={false} size="s">
                <tbody>
                  {isUnavailable ? (
                    <tr>
                      <td>Marked unavailable</td>
                      <td className={styles["table-date"]}>N/A</td>
                    </tr>
                  ) : null}
                  <tr>
                    <td>Voice added</td>
                    <td className={styles["table-date"]}>
                      {voice.addedAt?.toDateString() ?? "N/A"}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </AccordionPanel>
          </>
        )}
      </div>
    </>
  );
};

export default VoiceDrawer;
